/**
 * SECURITY: Validate branding URLs (logo, favicon) to prevent SSRF and XSS attacks
 *
 * This utility validates that URLs:
 * 1. Use HTTPS scheme only (prevents javascript:, data:, etc.)
 * 2. Point to trusted origins (Supabase storage only)
 * 3. Have valid structure
 */

// Trusted origins for branding assets
const TRUSTED_ORIGINS = [
  // Supabase storage - extract from environment or use pattern
  process.env.NEXT_PUBLIC_SUPABASE_URL
    ? new URL(process.env.NEXT_PUBLIC_SUPABASE_URL).origin
    : null,
].filter(Boolean) as string[];

// Also allow Supabase storage CDN pattern
const SUPABASE_STORAGE_PATTERN = /^https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\//;

/**
 * Validate that a URL is safe to use for branding assets
 * Returns the validated URL or null if invalid
 */
export function validateBrandingUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  // Must be a string
  if (typeof url !== 'string') {
    return null;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  // SECURITY: Block dangerous URI schemes
  const lowercaseUrl = trimmedUrl.toLowerCase();
  const dangerousSchemes = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
    'blob:',
  ];

  for (const scheme of dangerousSchemes) {
    if (lowercaseUrl.startsWith(scheme)) {
      console.warn(`Blocked dangerous URL scheme: ${scheme}`);
      return null;
    }
  }

  // SECURITY: Must start with https:// (no http:// in production)
  if (!trimmedUrl.startsWith('https://')) {
    // Allow http:// only for localhost development
    if (
      process.env.NODE_ENV === 'development' &&
      trimmedUrl.startsWith('http://localhost')
    ) {
      // OK for local dev
    } else {
      console.warn('Blocked non-HTTPS URL for branding');
      return null;
    }
  }

  // Try to parse as URL
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    console.warn('Invalid URL format for branding');
    return null;
  }

  // SECURITY: Validate origin is trusted
  const isTrustedOrigin = TRUSTED_ORIGINS.some(
    (origin) => parsedUrl.origin === origin
  );
  const isSupabaseStorage = SUPABASE_STORAGE_PATTERN.test(trimmedUrl);

  if (!isTrustedOrigin && !isSupabaseStorage) {
    console.warn(`Blocked untrusted origin for branding: ${parsedUrl.origin}`);
    return null;
  }

  // SECURITY: Block internal/private IPs (SSRF protection)
  const hostname = parsedUrl.hostname;
  const privatePatterns = [
    /^localhost$/i,
    /^127\./,
    /^10\./,
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
    /^192\.168\./,
    /^169\.254\./, // Link-local
    /^0\./, // Current network
    /^\[::1\]$/, // IPv6 localhost
    /^\[fc/, // IPv6 private
    /^\[fd/, // IPv6 private
    /^\[fe80/, // IPv6 link-local
    /^metadata\./, // Cloud metadata endpoints
    /^instance-data\./, // AWS metadata
  ];

  // Skip private IP check for Supabase URLs (they're already validated)
  if (!isSupabaseStorage) {
    for (const pattern of privatePatterns) {
      if (pattern.test(hostname)) {
        console.warn(`Blocked private/internal hostname: ${hostname}`);
        return null;
      }
    }
  }

  return trimmedUrl;
}

/**
 * Check if URL is a valid branding URL (boolean version)
 */
export function isValidBrandingUrl(url: string | null | undefined): boolean {
  return validateBrandingUrl(url) !== null;
}
